/**
 * Why a quotation was lost — the two masters behind the Reject dialog (Akhil, 2026-09-21):
 *
 *   GET  masters.get_lost_reasons?limit=50   → { data: [{name, order_lost_reason}], total }
 *   POST masters.create_lost_reason           { order_lost_reason }
 *   GET  masters.get_competitor?limit=50      → { data: [{name, competitor_name, website}], total }
 *   POST masters.create_competitor            { competitor_name, website }
 *
 * Measured 2026-09-21. A rejection may carry several reasons and several competitors, and the
 * dialog lets the purchaser add a new one of either without leaving it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { callGet, callPost } from './bosun'
import { str } from './wire'

export interface LostReason {
  name: string
  label: string
}
export interface Competitor {
  name: string
  label: string
  website: string
}

interface WireList<T> {
  data?: T[]
}

export function useLostReasons() {
  return useQuery({
    queryKey: ['masters', 'lost-reasons'] as const,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<LostReason[]> => {
      const w = await callGet<WireList<{ name?: unknown; order_lost_reason?: unknown }>>('masters', 'get_lost_reasons', { limit: 200 })
      return (w.data ?? []).map((r) => ({ name: str(r.name), label: str(r.order_lost_reason) || str(r.name) })).filter((r) => r.name !== '')
    },
  })
}

export function useCompetitors() {
  return useQuery({
    queryKey: ['masters', 'competitors'] as const,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Competitor[]> => {
      const w = await callGet<WireList<{ name?: unknown; competitor_name?: unknown; website?: unknown }>>('masters', 'get_competitor', { limit: 200 })
      return (w.data ?? []).map((r) => ({ name: str(r.name), label: str(r.competitor_name) || str(r.name), website: str(r.website) })).filter((r) => r.name !== '')
    },
  })
}

export function useCreateLostReason(options?: { onSuccess?: (reason: LostReason) => void; onError?: (error: Error) => void }) {
  const queryClient = useQueryClient()
  return useMutation<LostReason, Error, string>({
    mutationFn: async (text) => {
      const w = await callPost<{ data?: { name?: unknown; order_lost_reason?: unknown } }>('masters', 'create_lost_reason', { order_lost_reason: text.trim() })
      const name = str(w.data?.name)
      if (!name) throw new Error('The reason was not created — the server did not confirm it.')
      return { name, label: str(w.data?.order_lost_reason) || name }
    },
    onSuccess: (reason) => {
      void queryClient.invalidateQueries({ queryKey: ['masters', 'lost-reasons'] })
      options?.onSuccess?.(reason)
    },
    onError: (e) => options?.onError?.(e),
  })
}

export function useCreateCompetitor(options?: { onSuccess?: (competitor: Competitor) => void; onError?: (error: Error) => void }) {
  const queryClient = useQueryClient()
  return useMutation<Competitor, Error, { name: string; website: string }>({
    mutationFn: async ({ name, website }) => {
      const w = await callPost<{ data?: { name?: unknown; competitor_name?: unknown; website?: unknown } }>('masters', 'create_competitor', {
        competitor_name: name.trim(),
        website: website.trim(),
      })
      const docname = str(w.data?.name)
      if (!docname) throw new Error('The competitor was not created — the server did not confirm it.')
      return { name: docname, label: str(w.data?.competitor_name) || docname, website: str(w.data?.website) }
    },
    onSuccess: (c) => {
      void queryClient.invalidateQueries({ queryKey: ['masters', 'competitors'] })
      options?.onSuccess?.(c)
    },
    onError: (e) => options?.onError?.(e),
  })
}
